import { Loader2 } from "lucide-react";
import { useEffect, useRef, type RefObject } from "react";

interface InfiniteScrollProps {
  onLoadMore: () => void;
  hasMore: boolean;
  isLoading: boolean;
  className?: string;
  root?: RefObject<HTMLElement | null>;
  showLoader?: boolean;
  rootMargin?: string;
}

export function InfiniteScroll({
  onLoadMore,
  hasMore,
  isLoading,
  className,
  root,
  showLoader = false,
  rootMargin = "200px",
}: InfiniteScrollProps) {
  const observerTarget = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const target = observerTarget.current;
    if (!target || !hasMore || isLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          onLoadMore();
        }
      },
      {
        root: root?.current ?? null,
        rootMargin,
      },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [onLoadMore, hasMore, isLoading, root]);

  return (
    <div
      ref={observerTarget}
      className={className}
      style={{ overflowAnchor: "none" }}
    >
      {showLoader && (
        <div
          className={`flex justify-center py-2 ${isLoading ? "" : "invisible"}`}
        >
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        </div>
      )}
    </div>
  );
}
